import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import {
  type AddressType,
  BodyText,
  Button,
  IllusEmployeeBag,
  Image,
  LoadingPlaceholder,
  theme,
  useResponsive,
  VStack,
} from '@sg/garnish';
import { OrderSource } from '@sg/graphql-schema';

import { OrderCard } from '@order/components';
import { useLineItemsNames } from '@order/shared/hooks';
import { useTelemetry } from '@order/Telemetry';

import { type OrdersV2Query } from './GraphQL/Orders.generated';
import { usePaginatedOrdersWithMachine } from './hooks';
import { type OrderCancelationFields } from './orders-history-machine';

// ─────────────────────────────────────────────────────────────────────────────

export const Orders = () => {
  const {
    orders,
    isFetchingInitialOrders,
    isFetchingMoreOrders,
    canFetchMoreOrders,
    fetchMoreOrders,
    updateCancelledOrder,
  } = usePaginatedOrdersWithMachine();

  if (isFetchingInitialOrders) {
    return <OrdersLoadingView />;
  }

  return (
    <OrdersScreenContent
      orders={orders}
      fetchMoreOrders={fetchMoreOrders}
      isFetchingMoreOrders={isFetchingMoreOrders}
      onCancelOrder={updateCancelledOrder}
      canFetchMoreOrders={canFetchMoreOrders}
    />
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

export const OrdersScreenContent = (props: OrdersScreenContentProps) => {
  const {
    orders,
    canFetchMoreOrders,
    fetchMoreOrders,
    isFetchingMoreOrders,
    onCancelOrder,
  } = props;

  const { formatMessage } = useIntl();

  if (orders.length === 0) {
    return <EmptyState />;
  }

  return (
    <>
      <VStack accessibilityLabel={formatMessage(messages.ordersList)}>
        {orders.map((order) => (
          <ConnectedOrderCard
            key={order.id}
            order={order}
            onOrderCancellationSuccess={onCancelOrder}
          />
        ))}
      </VStack>

      {orders.length > 0 && canFetchMoreOrders ? (
        <Button
          palette="secondary"
          accessibilityRole="button"
          style={styles.button}
          isLoading={isFetchingMoreOrders}
          onPress={fetchMoreOrders}
        >
          <FormattedMessage {...messages.loadMore} />
        </Button>
      ) : null}
    </>
  );
};

const ConnectedOrderCard = (props: ConnectedOrderCardProps) => {
  const { order, onOrderCancellationSuccess } = props;

  const navigation = useNavigation();
  const { track } = useTelemetry();
  const orderLineItemsNames = useLineItemsNames(order?.lineItems);

  // ─── Flags ───────────────────────────────────────────────────────────

  const isOrderCanceled = Boolean(order?.isCanceled);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigateToOrderStatusScreen = React.useCallback(
    (orderId: string) => {
      navigation.navigate('Modal', {
        screen: 'OrderStatus',
        params: { orderId },
      });
    },
    [navigation],
  );

  const handlePress = useCallback(() => {
    navigateToOrderStatusScreen(order.id);
    track('order_status.view', { entryPoint: 'account.orders' });
  }, [order.id, navigateToOrderStatusScreen, track]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <OrderCard
      orderId={order.id}
      orderType={convertOrderType(order)}
      wantedTime={order.wantedTime}
      restaurantId={order.restaurant.id}
      restaurantName={order.restaurant.name}
      addressName={order.deliveryOrderDetail?.address?.name ?? undefined}
      description={orderLineItemsNames}
      total={order.total ?? 0}
      assetUrl={order.asset?.url ?? ''}
      isCancellable={order.canCancel}
      isCanceled={isOrderCanceled}
      onPress={handlePress}
      onOrderCancellationSuccess={onOrderCancellationSuccess}
    />
  );
};

const EmptyState = () => {
  const { formatMessage } = useIntl();

  return (
    <View style={styles.emptyContainer}>
      <Image
        source={IllusEmployeeBag}
        style={styles.emptyImage}
        aria-label={formatMessage(messages.emptyIllustrationAlt)}
      />

      <BodyText style={styles.noResultsText}>
        <FormattedMessage {...messages.empty} />
      </BodyText>
    </View>
  );
};

const OrdersLoadingView = () => {
  const { match } = useResponsive();

  return (
    <LoadingPlaceholder
      palette="cream"
      rowHeight={ORDER_CARD_HEIGHT}
      rows={5}
      gridGap={match([theme.spacing[4], theme.spacing[6]])}
    />
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function convertOrderType(order: Order): AddressType {
  if (order.orderSource === OrderSource.Kiosk) {
    return 'kiosk';
  }

  if (order.restaurant.isOutpost) {
    return 'outpost';
  }

  return order.orderType.toLowerCase() as AddressType;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const ORDER_CARD_HEIGHT = 170;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  ordersList: {
    defaultMessage: 'Orders list',
    description: 'Account | Orders | Label for the orders list section',
  },
  loadMore: {
    defaultMessage: 'See more orders',
    description: 'Account | Orders | Button text to load more orders',
  },
  emptyIllustrationAlt: {
    defaultMessage: 'A person holding a Sweetgreen bag',
    description: 'Account | Orders | Alt text for empty state illustration',
  },
  empty: {
    defaultMessage: 'Once you place an order your history will appear here',
    description: 'Account | Orders | Text shown when no orders are available',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyImage: {
    width: 220,
    height: 220,
    marginBottom: theme.spacing['4'],
  },
  noResultsText: {
    textAlign: 'center',
  },
  button: {
    minWidth: 200,
    maxWidth: 200,
    marginHorizontal: 'auto',
    marginTop: theme.spacing['6'],
  },
});

// ─── Types ─────────────────────────────────────────────────────────────

type OrdersScreenContentProps = Readonly<{
  orders: readonly OrderWithCancelationFields[];
  canFetchMoreOrders: boolean;
  isFetchingMoreOrders: boolean;
  onCancelOrder: (orderId: string) => void;
  fetchMoreOrders: () => void;
}>;

type ConnectedOrderCardProps = Readonly<{
  order: OrderWithCancelationFields;
  onOrderCancellationSuccess: (orderId: string) => void;
}>;

type OrderWithCancelationFields = Order & OrderCancelationFields;

type Order = PlacedOrders[number];

type PlacedOrders = NonNullable<
  Extract<
    OrdersV2Query['ordersV2'],
    { __typename: 'OrdersResponseSuccess' }
  >['orders']
>;
